import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Replace, Bot, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import StarCanvas from './StarCanvas';

const tools = [
  "LinkedIn Sales Navigator",
  "Apollo.io",
  "Outreach.io",
  "ZoomInfo",
  "SalesLoft",
  "Lusha"
];

const processSteps = [
  {
    name: 'Define & Find',
    description: 'AI defines your ICP and discovers matching prospects automatically',
    icon: '🎯'
  },
  {
    name: 'Research',
    description: 'Deep-dive research on each lead for hyper-personalization',
    icon: '🔍'
  },
  {
    name: 'Engage',
    description: 'Personalized multi-channel outreach at scale',
    icon: '✉️'
  },
  {
    name: 'Convert',
    description: 'Smart follow-ups and meeting scheduling',
    icon: '🤝'
  }
];

const CompleteSolution = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <StarCanvas />
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Complete Sales Stack In One
          </h2>
          <p className="text-gray-400 text-xl max-w-2xl mx-auto">
            Everything your BDRs need to excel, in a single intelligent platform
          </p>
        </motion.div>

        {/* Main Cards */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {/* Power Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative p-[1px] rounded-2xl overflow-hidden group"
          >
            {/* Gradient border */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-fuchsia-500 opacity-20 group-hover:opacity-30 transition-opacity" />
            
            {/* Content */}
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl p-8 h-full">
              <div className="text-purple-400 mb-4">
                <Zap className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Turn Junior BDRs into Top Performers
              </h3>
              <ul className="text-gray-400 space-y-2">
                <li>• AI-guided prospecting</li>
                <li>• Smart message crafting</li>
                <li>• Automated follow-ups</li>
                <li>• Performance optimization</li>
              </ul>
            </div>
          </motion.div>

          {/* Replacement Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative p-[1px] rounded-2xl overflow-hidden group"
          >
            {/* Gradient border */}
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-pink-500 opacity-20 group-hover:opacity-30 transition-opacity" />
            
            {/* Content */}
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl p-8 h-full">
              <div className="text-purple-400 mb-4">
                <Replace className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                No More Tool Juggling
              </h3>
              <p className="text-gray-400 mb-4">
                Replace your entire stack with one unified workflow
              </p>
              <div className="flex flex-wrap gap-2">
                {tools.map((tool, index) => (
                  <span key={index} className="px-2 py-1 bg-purple-500/10 rounded-full text-sm text-purple-200">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Results Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="relative p-[1px] rounded-2xl overflow-hidden group"
          >
            {/* Gradient border */}
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-purple-500 opacity-20 group-hover:opacity-30 transition-opacity" />
            
            {/* Content */}
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl p-8 h-full">
              <div className="text-purple-400 mb-4">
                <Bot className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                End-to-End Process
              </h3>
              <p className="text-gray-400">
                One intelligent system handles everything from research to booking
              </p>
            </div>
          </motion.div>
        </div>

        {/* Process Flow */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mt-24"
        >
          <div className="relative">
            {/* Process Steps */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-16 relative">
              {/* Continuous connecting line */}
              <div className="hidden md:block absolute top-[40px] left-[60px] right-[60px] h-[2px] bg-purple-600/20">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-purple-500 to-purple-600 animate-pulse" />
                
                {/* Arrows on the line */}
                <div className="absolute top-1/2 left-[28%] -translate-y-1/2">
                  <div className="relative">
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-full bg-purple-500/50 blur-md" />
                  </div>
                </div>
                
                <div className="absolute top-1/2 left-[61%] -translate-y-1/2">
                  <div className="relative">
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-full bg-purple-500/50 blur-md" />
                  </div>
                </div>
                
                <div className="absolute top-1/2 left-[94%] -translate-y-1/2">
                  <div className="relative">
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-full bg-purple-500/50 blur-md" />
                  </div>
                </div>
              </div>

              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="text-center relative z-10">
                    {/* Icon Container */}
                    <div className="relative w-20 h-20 mx-auto mb-4">
                      {/* Background square with glow */}
                      <div className="absolute inset-0 bg-purple-600 rounded-2xl transform rotate-45" />
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-purple-500/30 rounded-2xl transform rotate-45 blur-lg" />
                      {/* Icon wrapper */}
                      <div className="absolute inset-0 flex items-center justify-center text-2xl">
                        {step.icon}
                      </div>
                    </div>

                    {/* Step Name */}
                    <h4 className="text-white font-semibold text-lg mb-2">
                      {step.name}
                    </h4>
                    {/* Description */}
                    <p className="text-gray-400 text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile connecting line */}
            <div className="md:hidden absolute left-1/2 top-20 h-[80%] w-[2px] bg-purple-600/20">
              <div className="absolute inset-0 bg-gradient-to-b from-purple-600 via-purple-500 to-transparent" />
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <button className="px-8 py-4 bg-purple-600 rounded-xl text-white font-semibold hover:bg-purple-500 transition-all">
            Get Started Now
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CompleteSolution; 